import { Suspense, useEffect, useState } from "react";
import Loading from "../../../common/components/ui/loading/Loading";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import ModalCreateVehicleTransfer from "./modals/ModalCreateVehicleTransfer";
import {
  IVehicleTransfer,
  VEHICLE_TRANSFER_STATUS,
} from "@/types/vehicleTransfer.type";
import { getVehiclesTransfers } from "../../../common/services/vehiclesTransfer.api";
import GenericTable from "@/common/components/ui/table/GenericTable";
import { getCompanyName } from "@/common/helpers/company.helpers";
import { formatDateAndTime } from "@/common/helpers/date.helpers";
import ModalEditVehicle from "./modals/ModalEditVehicle";
import ModalConfirmTransfer from "./modals/ModalConfirmTransfer";
import { useCompanyStore } from "@/common/states/store/Company";

const headers = {
  companyOriginId: "Cidade",
  companyDestinyId: "Cidade Destino",
  total: "Total",
  transferDate: "Data da trasnferencia",
  arrivalDate: "Data da chegada",
  status: "Status",
  updatedAt: "Atualizado em",
  createdAt: "Criado em",
};

// eslint-disable-next-line react-refresh/only-export-components
export const STATUS_LABELS = {
  [VEHICLE_TRANSFER_STATUS.IN_PROGRESS]: "Em andamento",
  [VEHICLE_TRANSFER_STATUS.COMPLETED]: "Concluído",
};

const EMPTY_ACTION = { eventName: "", data: undefined };

function VehicleTransferTable() {
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const { notifyError } = useToastContext();
  const company = useCompanyStore();
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [transfers, setTransfers] = useState<IVehicleTransfer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "",
  });
  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };
  const onVehicleTransferCreated = () => {
    setFilters({ ...filters });
    setAction(EMPTY_ACTION);
  };

  const isAction = (eventName: string) => action.eventName === eventName;
  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      setTransfers([]);

      const result = await getVehiclesTransfers({
        ...filters,
        page,
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.data.length,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        setIsLoading(false);
        return;
      }

      setTransfers(result.data);

      setIsLoading(false);
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, company]);

  const actions = [
    {
      label: "Adicionar/Editar Veículos",
      action: (data) =>
        setAction({ eventName: "edit", data: data as IVehicleTransfer }),
      permission: "edit:vehicles",
    },
    {
      label: "Confirmar Chegada",
      action: (data) =>
        setAction({ eventName: "confirm", data: data as IVehicleTransfer }),
      permission: "edit:vehicles",
      visible: (data) => {
        const transfer = data as IVehicleTransfer;
        const isStatusPending =
          transfer.status !== VEHICLE_TRANSFER_STATUS.COMPLETED;
        const isUserInDestinyCompany = company.selectedCompanies.some(
          (selected) => selected.companyId === transfer.companyDestinyId
        );

        return isStatusPending && isUserInDestinyCompany;
      },
    },
  ];

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Transferências de Veículos"
          placeholder="Digite a cidade origem, cidade destino, o total ou o status."
          statusOptions={STATUS_LABELS}
          newItemLabel="Nova Transferência"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleSearch}
          entity="vehicles"
          tableData={transfers as unknown as Record<string, unknown>[]}
        />
        <GenericTable
          headers={headers}
          data={transfers as unknown as Record<string, unknown>[]}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({ ...prevFilters, orderBy }));
          }}
          renders={{
            companyOriginId: (companyId) => getCompanyName(companyId as string),
            companyDestinyId: (companyId) =>
              getCompanyName(companyId as string),
            status: (status) =>
              STATUS_LABELS[status as keyof typeof STATUS_LABELS],
            total: (_, transfer) =>
              (transfer as IVehicleTransfer).vehicles?.length || 0,
            transferDate: (date) => formatDateAndTime(date as string),
            arrivalDate: (date) =>
              date ? formatDateAndTime(date as string) : "—",
            updatedAt: (date) => formatDateAndTime(date as string),
            createdAt: (date) => formatDateAndTime(date as string),
          }}
          entity="vehicles"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("create") && (
          <ModalCreateVehicleTransfer
            onClose={cleanActionData}
            onSave={onVehicleTransferCreated}
          />
        )}
        {isAction("edit") && (
          <ModalEditVehicle
            onClose={cleanActionData}
            onSave={cleanActionData}
            transfer={action.data as IVehicleTransfer}
          />
        )}
        {isAction("confirm") && (
          <ModalConfirmTransfer
            onClose={cleanActionData}
            onSave={cleanActionData}
            transfer={action.data as IVehicleTransfer}
          />
        )}
      </div>
    </Suspense>
  );
}

export default VehicleTransferTable;
